import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Upload, Camera, FileText, Check, Clock, X, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';

const STATUS_META = {
    ocr_processing: { label: 'Reading bill…', icon: Clock, color: 'text-amber-700' },
    pending_confirmation: { label: 'Awaiting your confirmation', icon: Clock, color: 'text-amber-700' },
    pending_approval: { label: 'Pending approval', icon: Clock, color: 'text-amber-700' },
    approved: { label: 'Approved', icon: Check, color: 'text-emerald-700' },
    rejected: { label: 'Rejected', icon: X, color: 'text-rose-700' },
    fraud_review: { label: 'Under review', icon: AlertTriangle, color: 'text-orange-700' },
};

export default function Bills() {
    const { type, loading, user } = useAuth();
    const [malls, setMalls] = useState([]);
    const [brands, setBrands] = useState([]);
    const [bills, setBills] = useState([]);
    const [mall, setMall] = useState('');
    const [brand, setBrand] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState('');
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get('/malls').then(({ data }) => setMalls(data));
        api.get('/brands?limit=200').then(({ data }) => setBrands(data));
        if (type === 'customer') api.get('/bills/me').then(({ data }) => setBills(data));
    }, [type]);

    const onPick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5MB)'); return; }
        setFile(f);
        const reader = new FileReader();
        reader.onload = (ev) => setPreview(ev.target.result);
        reader.readAsDataURL(f);
    };

    const upload = async () => {
        if (!mall) { toast.error('Choose a mall'); return; }
        if (!preview) { toast.error('Add a bill image'); return; }
        setBusy(true);
        setResult(null);
        try {
            const { data } = await api.post('/bills/upload', {
                mall_id: mall, brand_id: brand || null, image_base64: preview,
            });
            setResult(data);
            toast.success(`Bill captured — ${data.points_to_credit} points pending`);
            const refresh = await api.get('/bills/me');
            setBills(refresh.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Upload failed');
        } finally { setBusy(false); }
    };

    const confirmBill = async (id) => {
        try {
            const { data } = await api.post(`/bills/${id}/confirm`);
            toast.success(data.status === 'approved' ? 'Approved — points credited!' : 'Submitted for approval');
            setResult(data);
            const refresh = await api.get('/bills/me');
            setBills(refresh.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not confirm');
        }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
    if (type !== 'customer') return <Navigate to="/login?next=/bills" replace />;

    const brandOptions = brands.filter((b) => !mall || b.mall_ids.includes(mall));

    return (
        <div className="pt-16 pb-32" data-testid="bills-page">
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-12 mb-12">
                <div className="overline mb-4">Upload Bill</div>
                <h1 className="font-serif text-5xl md:text-6xl text-navy leading-tight">Earn points <span className="italic text-gold">automatically.</span></h1>
                <p className="mt-6 text-text-secondary text-lg max-w-2xl">Snap a photo of your bill. Our AI reads it. Points appear in your wallet — no manual entry, no friction.</p>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-10">
                {/* UPLOAD */}
                <div className="card-luxury p-10" data-testid="bill-upload-card">
                    <div className="overline mb-6">New Upload</div>
                    <div className="space-y-5">
                        <div>
                            <label className="text-xs uppercase tracking-widest text-text-tertiary block mb-2">Mall</label>
                            <select className="input-box" value={mall} onChange={(e) => { setMall(e.target.value); setBrand(''); }} data-testid="bill-mall-select">
                                <option value="">Choose a mall</option>
                                {malls.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-text-tertiary block mb-2">Brand / Store (optional)</label>
                            <select className="input-box" value={brand} onChange={(e) => setBrand(e.target.value)} disabled={!mall} data-testid="bill-brand-select">
                                <option value="">Select brand (or let AI detect)</option>
                                {brandOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-widest text-text-tertiary block mb-2">Bill Image</label>
                            <label className="border-2 border-dashed border-border-medium hover:border-gold transition-colors block aspect-[4/3] cursor-pointer overflow-hidden" data-testid="bill-image-dropzone">
                                <input type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="bill-image-input" />
                                {preview ? (
                                    <img src={preview} alt="" className="w-full h-full object-contain" />
                                ) : (
                                    <div className="h-full flex flex-col items-center justify-center text-text-tertiary text-sm">
                                        <Camera size={36} strokeWidth={1.3} className="mb-3 text-gold" />
                                        <span>Click to upload bill image</span>
                                        <span className="text-xs mt-1">JPG, PNG • max 5MB</span>
                                    </div>
                                )}
                            </label>
                        </div>
                        <button className="btn-primary w-full justify-center" onClick={upload} disabled={busy || !preview || !mall} data-testid="bill-upload-btn">
                            {busy ? 'Reading with AI…' : <><Upload size={16} /> Upload & Process</>}
                        </button>
                    </div>
                </div>

                {/* RESULT */}
                <div className="card-luxury p-10" data-testid="bill-result-card">
                    <div className="overline mb-6">AI Extracted Data</div>
                    {result ? (
                        <div className="space-y-4">
                            <Row label="Status" value={STATUS_META[result.status]?.label || result.status} />
                            <Row label="Store" value={result.brand_name || '—'} />
                            <Row label="Mall" value={result.mall_name} />
                            <Row label="Invoice Number" value={result.invoice_number || '—'} />
                            <Row label="Invoice Date" value={result.invoice_date || '—'} />
                            <Row label="Amount" value={result.total_amount ? `₹${result.total_amount.toLocaleString('en-IN')}` : '—'} />
                            <Row label="GST" value={result.gst_number || '—'} />
                            <Row label="OCR Confidence" value={`${result.ocr_confidence?.toFixed(0)}%`} />
                            <div className="border-t border-border-subtle pt-4">
                                <div className="text-xs uppercase tracking-widest text-text-tertiary mb-2">Points to be credited</div>
                                <div className="font-serif text-4xl text-gold">+{result.points_to_credit}</div>
                            </div>
                            {result.fraud_flags?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                                    Flagged: {result.fraud_flags.join(', ')}. Manual review may apply.
                                </div>
                            )}
                            {result.status === 'pending_confirmation' && (
                                <button className="btn-gold w-full justify-center mt-4" onClick={() => confirmBill(result.id)} data-testid="bill-confirm-btn">
                                    Confirm & Submit
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-text-tertiary text-sm">
                            <FileText size={48} strokeWidth={1} className="mx-auto mb-4 text-gold/50" />
                            Upload a bill to see AI-extracted data here.
                        </div>
                    )}
                </div>
            </section>

            {/* HISTORY */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20" data-testid="bill-history-section">
                <div className="overline mb-4">Your Bill History</div>
                <h3 className="font-serif text-3xl text-navy mb-8">Recent uploads</h3>
                <div className="card-luxury divide-y divide-border-subtle">
                    {bills.length === 0 ? (
                        <div className="p-12 text-center text-text-tertiary text-sm">No bills uploaded yet.</div>
                    ) : bills.map((b) => {
                        const meta = STATUS_META[b.status] || STATUS_META.pending_approval;
                        const Icon = meta.icon;
                        return (
                            <div key={b.id} className="px-6 py-5 flex items-center justify-between">
                                <div>
                                    <div className="text-sm text-navy font-medium">{b.brand_name || 'Store'} — ₹{(b.total_amount || 0).toLocaleString('en-IN')}</div>
                                    <div className="text-xs text-text-tertiary mt-1">{b.mall_name} • {new Date(b.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</div>
                                </div>
                                <div className={`flex items-center gap-2 text-xs uppercase tracking-widest ${meta.color}`}>
                                    <Icon size={14} strokeWidth={1.5} /> {meta.label}
                                    {b.status === 'approved' && <span className="ml-3 text-gold font-mono">+{b.points_credited}</span>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between text-sm">
            <span className="text-text-tertiary uppercase tracking-widest text-xs">{label}</span>
            <span className="text-navy font-medium">{value}</span>
        </div>
    );
}
